// ─────────────────────────────────────────────────────────────────────────
// clearGroupOnChange — "when THIS field changes, the rest of its group no
// longer describes anything".
//
// THE REQUIREMENT
// "After the new employer is added by clicking Add More in the submission and
//  candidate form, after successful add it selects the employer name. If the
//  employer name changes, reset all the other fields in the employer group."
//
// The employer group's other fields — recruiter, email, contact code, contact,
// VMS %, tax — all describe the PREVIOUSLY selected employer. Leaving them
// behind after the employer changes silently attaches one company's recruiter
// and phone number to another company, which is worse than a blank form
// because it looks filled in and correct.
//
// `linkedClearField` already existed but clears exactly ONE named sibling and
// only from a checkbox. This generalises it: any field may declare
// `clearGroupOnChange: true` to clear every OTHER field of its own group, and
// `linkedClearField` may now name several fields.
//
// Row-scoped inside a repeatable group: clearing employer row 2 must not touch
// row 1.
// ─────────────────────────────────────────────────────────────────────────

/**
 * siblingPath — the form path of `key` as a sibling of the field at `name`.
 * Inside a Form.List row, `name` is [listName, rowIndex, fieldKey], so the
 * sibling shares the first two segments and only the last changes.
 */
export function siblingPath(name, key) {
  return Array.isArray(name) && name.length > 1
    ? [...name.slice(0, -1), key]
    : [key];
}

/**
 * fieldsToClear — which keys this change should blank.
 *
 * @param {object} field        the field that changed
 * @param {Set|Array} groupKeys every field key in the field's group
 * @returns {string[]} keys to clear, never including the field itself
 */
export function fieldsToClear(field, groupKeys) {
  const own = field?.field;
  const keys = [];

  if (field?.clearGroupOnChange) {
    const all = groupKeys instanceof Set ? [...groupKeys] : (groupKeys ?? []);
    all.forEach((key) => { if (key && key !== own) keys.push(key); });
  }

  // linkedClearField now accepts one key or several. A field named here is
  // cleared even when it is NOT part of the group, which is what makes it
  // usable for a cross-group dependency.
  const linked = field?.linkedClearField;
  if (Array.isArray(linked)) {
    linked.forEach((key) => { if (key && key !== own) keys.push(key); });
  } else if (typeof linked === 'string' && linked.trim() && linked.trim() !== own) {
    keys.push(linked.trim());
  }

  return [...new Set(keys)];
}

/**
 * applyGroupClear — perform the clear.
 *
 * Values are set to `null` rather than deleted: antd keeps a Form.Item
 * registered either way, and null is what every other clear path in these forms
 * writes, so a subsequent payload build treats it identically.
 *
 * Returns the paths cleared, so a caller can re-validate or test.
 */
export function applyGroupClear(form, field, name, groupKeys) {
  const keys = fieldsToClear(field, groupKeys);
  const paths = keys.map((key) => siblingPath(name, key));
  paths.forEach((path) => form.setFieldValue(path, null));
  return paths;
}
